import type { MeshMetrics } from '../../lib/meshMetrics'
import type { SourceModel } from '../../lib/modelPipeline'
import type { ParsedModelPart, ParsedSourceColor } from '../../lib/parseModel'
import type { MaterialId } from '../../lib/catalog'
import type { ActiveCourier, HomeCourier, PointType, ShippingService } from '../../lib/shipping'
import type { DeliveryPoint } from '../../lib/kvikk'

/**
 * A LEPESEK. A sorrend a folyamat sorrendje.
 *
 * ────────────────────────────────────────────────────────────────────
 * EGY LEPES = EGY DONTES.
 *
 * Korabban egyetlen "Beallitasok" lepes kerte egyszerre az anyagot, a szint,
 * a minoseget, a darabszamot, a meretezest es a simitast. Aki nem 3D-s - es a
 * latogatok tobbsege nem az -, annak ez egy fal volt: hat ismeretlen dontes
 * egyszerre, barmelyiknel el lehetett akadni.
 *
 * Most harom kulon lepes all a helyen. Mindegyik EGY kerdest tesz fel, es
 * mindegyikre van jo alapertelmezes, tehat tovabb lehet lepni valasztas
 * nelkul is. A hosszabb lista nem tobb munkat jelent, hanem kisebb falatokat.
 * ────────────────────────────────────────────────────────────────────
 *
 * ────────────────────────────────────────────────────────────────────
 * A FIZETOS EXTRAK EGY HELYEN, A LEADAS PEDIG A LEGVEGEN.
 *
 * A simitas a "Minoseg", az Expressz az "Atvetel" lepesen bujt meg. Aki
 * arat hasonlitott ossze, ket kulon kepernyot kellett vegigjarnia ahhoz,
 * hogy lassa, mi az, amiert felarat fizet - es epp az a ket kepernyo volt
 * ez, ahol mas dontes is varta. Az `EXTRAK` lepes osszefogja oket: aki nem
 * ker semmit, egy kattintassal tovabblep, aki ker, egy helyen lat mindent.
 *
 * A zaro `OSSZEGZES` lepes pedig a MEGEROSITES helye. Korabban a kuldes
 * gomb a kapcsolati urlap aljan allt, tehat a latogato ugy kuldte el az
 * ajanlatkerest, hogy kozben a sajat nevet gepelte - nem azt olvasta, mit
 * rendelt. Most eloszor lat mindent, aztan kuld.
 * ────────────────────────────────────────────────────────────────────
 */
export const STEPS = [
  { id: 'modellek', title: 'Modelljeim', short: 'Modellek' },
  { id: 'beallitasok', title: 'Beállítások és ár', short: 'Beállítások' },
  { id: 'bekuldes', title: 'Kapcsolat és beküldés', short: 'Beküldés' }
] as const

export type StepIndex = 0 | 1 | 2

/**
 * A LEPESEK HAROM SZAKASZBA VONVA - AMIT A LATOGATO LAT.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT NEM A NYOLC LEPES LATSZIK
 *
 * A nyolc kepernyo BELUL marad, es ez jo igy: mindegyik EGY kerdest tesz
 * fel (lasd a `STEPS` fenti indoklasat). Kifele viszont a „1/8. lepes"
 * felirat mast kozol, mint amit a folyamat er: egy hideg latogatonak, aki
 * most kattintott ide egy hirdetesbol, a nyolcas szam HOSSZU elkotelezodest
 * jelez, meg mielott barmit is latott volna az arbol.
 *
 * A merheto ok: 2026-08-21-en 130 latogato nyitotta meg a kalkulatort, es
 * 33-ig jutott el az elso sikeres fajlbeolvasasig. A nyolcas szam ott allt
 * a kepernyo tetejen, mielott barki egyetlen fajlt is feltoltott volna.
 *
 * A szakaszok tehat NEM vonjak ossze a lepeseket, csak CSOPORTOSITJAK oket.
 * A `from`/`to` a `STEPS` indexeire mutat, es a `wizardGroups.test.ts`
 * ellenorzi, hogy a harom szakasz hezag es atfedes nelkul lefedi mind a
 * nyolcat - ha valaki uj lepest vesz fel es ide elfelejti bevenni, a teszt
 * bukik, nem a felulet romlik el csendben.
 * ────────────────────────────────────────────────────────────────────
 */
export const STEP_GROUPS = [
  { id: 'fajl', label: 'Fájl', from: 0, to: 0 },
  { id: 'beallitasok', label: 'Beállítások és ár', from: 1, to: 1 },
  { id: 'ajanlatkeres', label: 'Kapcsolat és beküldés', from: 2, to: 2 }
] as const

export type StepGroupId = (typeof STEP_GROUPS)[number]['id']

/** Melyik szakaszban all az adott lepes. Sosem `-1`: a szakaszok lefedik a `STEPS`-et. */
export function groupIndexFor(step: StepIndex): number {
  return step
}

/**
 * BIZTONSAGOS ALAPERTELMEZESEK.
 *
 * A latogatonak NEM kell minden modellhez minden adatot megadnia. Ezek az
 * ertekek a leggyakoribb, legkevesebb meglepetest okozo valasztasok - es
 * barmikor felulirhatok.
 */
export const DEFAULT_SETTINGS: ModelSettings = {
  material: 'PETG',
  colorId: 'petg-black',
  qualityId: 'petg-altalanos',
  quantity: 1,
  scale: 1,
  // A simitas FIZETOS extra, ezert alapbol ki. Amit a latogato kulon
  // megfizet, azt kulon is kell kernie.
  ironing: false
}

export interface ModelSettings {
  material: MaterialId
  colorId: string
  qualityId: string
  quantity: number
  scale: number
  /**
   * Simitas (ironing) a felfele nezo, vizszintes feluleteken.
   *
   * FIZETOS extra: a simitott tetelek gyartasi ertekenek szazaleka, also
   * hatarral (`estimation_params.ironing`). Az osszesitoben mindig kulon
   * soron latszik, hogy ne lehessen eszrevetlenul benne felejteni.
   */
  ironing: boolean
}

export interface WizardItem extends ModelSettings {
  id: string
  source: SourceModel
  /** `null`, amig tolt vagy ha nem sikerult beolvasni. */
  positions: Float32Array | null
  /** A 3MF eredeti RGB csucsszinei; mas formatumnal `null`. */
  vertexColors?: Float32Array | null
  /** A 3MF-ben talalt kulonbozo szinek szama. */
  sourceColorCount?: number
  /** Az eredeti 3MF-paletta filament-sorrendben. */
  sourceColors?: ParsedSourceColor[]
  /** A 3MF kulon mozgathato nyomtathato elemei. */
  parts?: ParsedModelPart[]
  metrics: MeshMetrics | null
  loading: boolean
  error: string | null
  /** Az elonezet elbukott, de a modell muszaki feldolgozasa sikerult. */
  previewFailed: boolean
  note: string
  /** Egyedi beallitast kapott-e, vagy a kozos beallitast koveti. */
  custom: boolean
  upload: 'idle' | 'hashing' | 'uploading' | 'done' | 'failed'
  progress: number
}

export type DeliveryKind = 'parcel_point' | 'home' | 'personal'

export interface DeliveryChoice {
  kind: DeliveryKind
  /** Csomagpontnal barmelyik aktiv futar, hazhozszallitasnal csak GLS/MPL/DPD. */
  courier: ActiveCourier | HomeCourier | null
  pointType: PointType | null
  point: DeliveryPoint | null
  /** „Csomagpontot később választok" - az ajanlatkeres igy is elmegy. */
  pointLater: boolean
}

export const EMPTY_DELIVERY: DeliveryChoice = {
  kind: 'parcel_point',
  courier: null,
  pointType: null,
  point: null,
  pointLater: false
}

/**
 * A valasztasbol szallitasi szolgaltatas.
 *
 * `null`, ha meg nincs eleg adat - ilyenkor a szallitas 0, es az
 * osszesito ezt ki is irja ("még nincs kiválasztva").
 */
export function serviceFor(choice: DeliveryChoice): ShippingService | null {
  if (choice.kind === 'personal') return { kind: 'personal' }
  if (!choice.courier) return null

  if (choice.kind === 'home') {
    return { kind: 'home', courier: choice.courier as HomeCourier }
  }
  if (!choice.pointType) return null
  return {
    kind: 'parcel_point',
    courier: choice.courier as ActiveCourier,
    pointType: choice.pointType
  }
}
